import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Trash2, Wine, Beer, Utensils, Package, ShoppingBag, Sparkles, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import UpsellStrip from "../components/UpsellStrip";
import PushToggle from "../components/PushToggle";
import { useAuth } from "../contexts/AuthContext";

const CATS = [
  { key: "cocktails", label: "Cocktails", icon: Wine, endpoint: "/cocktails", kind: "cocktail" },
  { key: "beers",     label: "Bières",    icon: Beer, endpoint: "/beers",     kind: "beer" },
  { key: "snacks",    label: "Snacks",    icon: Utensils, endpoint: "/snacks", kind: "snack" },
];

const euro = (n) => `${Number(n || 0).toFixed(2)} €`;

/** Mobile self-service ordering, reached by scanning the QR sticker on
 * a table. Auth is deferred until checkout so the customer can browse the
 * menu even before signing in. */
export default function TableMenu() {
  const { num } = useParams();
  const tableNum = parseInt(num, 10);
  const nav = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState({ cocktails: [], beers: [], snacks: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cocktails");
  const [cart, setCart] = useState({}); // { key: {kind, ref_id, name, unit_price, quantity} }
  const [checkingOut, setCheckingOut] = useState(false);
  const [vouchers, setVouchers] = useState([]);       // available loyalty vouchers
  const [chosenVoucher, setChosenVoucher] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [barmanMessage, setBarmanMessage] = useState("");

  useEffect(() => {
    if (!tableNum || tableNum < 1 || tableNum > 20) return;
    (async () => {
      try {
        const [c, b, s, ct] = await Promise.all([
          api.get("/cocktails"), api.get("/beers"), api.get("/snacks"),
          api.get("/content"),
        ]);
        setCatalog({ cocktails: c.data, beers: b.data, snacks: s.data });
        setBarmanMessage((ct.data?.qr_barman_message || "").trim());
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Erreur menu");
      } finally { setLoading(false); }
    })();
  }, [tableNum]);

  // Pull the user's loyalty state (balance + unspent vouchers) once auth'd.
  useEffect(() => {
    if (!user) { setVouchers([]); setPointsBalance(0); return; }
    (async () => {
      try {
        const { data } = await api.get("/loyalty/me");
        setPointsBalance(data.account?.points_balance || 0);
        setVouchers((data.vouchers || []).filter((v) => Number(v.current_balance || 0) > 0));
      } catch (e) {
        // Loyalty is optional context — don't disturb the ordering flow
        // if the endpoint is momentarily unreachable, but log so we notice.
        console.warn("[TableMenu] /loyalty/me failed", e);
      }
    })();
  }, [user]);

  const items = catalog[tab] || [];

  const add = (item, kind) => {
    const key = `${kind}:${item.id}`;
    setCart((prev) => {
      const existing = prev[key];
      const quantity = (existing?.quantity || 0) + 1;
      if (quantity > 20) { toast.error("Max 20 par item"); return prev; }
      return {
        ...prev,
        [key]: {
          kind, ref_id: item.id, name: item.name,
          unit_price: Number(item.price || 0), quantity,
        },
      };
    });
  };
  const bump = (key, delta) => {
    setCart((prev) => {
      const line = prev[key];
      if (!line) return prev;
      const q = line.quantity + delta;
      if (q <= 0) { const { [key]: _, ...rest } = prev; return rest; }
      return { ...prev, [key]: { ...line, quantity: q } };
    });
  };

  const lines = useMemo(() => Object.values(cart), [cart]);
  const subtotal = useMemo(
    () => lines.reduce((acc, l) => acc + l.unit_price * l.quantity, 0),
    [lines],
  );
  const discount = useMemo(() => {
    if (!chosenVoucher) return 0;
    return Math.min(Number(chosenVoucher.current_balance || 0), subtotal);
  }, [chosenVoucher, subtotal]);
  const total = Math.max(0, subtotal - discount);
  const totalItems = useMemo(() => lines.reduce((a, l) => a + l.quantity, 0), [lines]);

  const [firstName, setFirstName] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);

  const openCheckoutModal = () => {
    if (lines.length === 0) return;
    if (!user) {
      const next = encodeURIComponent(window.location.pathname);
      toast.info("Connectez-vous pour valider votre commande");
      nav(`/connexion?next=${next}`);
      return;
    }
    // Prefill prénom from the account (customer can still tweak).
    if (!firstName && user?.name) setFirstName(user.name.split(" ")[0]);
    setShowCheckout(true);
  };

  const checkout = async () => {
    if (lines.length === 0) return;
    if (!user) {
      const next = encodeURIComponent(window.location.pathname);
      toast.info("Connectez-vous pour valider votre commande");
      nav(`/connexion?next=${next}`);
      return;
    }
    const trimmedName = firstName.trim();
    if (!trimmedName) {
      toast.error("Merci de saisir votre prénom");
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await api.post("/client-orders/checkout", {
        table_num: tableNum,
        lines: lines.map((l) => ({
          kind: l.kind, ref_id: l.ref_id, quantity: l.quantity,
        })),
        origin_url: window.location.origin,
        voucher_code: chosenVoucher?.code || null,
        first_name: trimmedName,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setCheckingOut(false); }
  };

  if (!tableNum || tableNum < 1 || tableNum > 20) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center">
        <div>
          <h1 className="font-display text-3xl mb-2">Table inconnue</h1>
          <p className="text-sm text-muted-foreground">Le QR code scanné pointe vers une table qui n'existe pas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" data-testid="table-menu-page">
      {/* Header sticky */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Saveurs Normandes</div>
            <div className="font-display text-2xl leading-none">Table {tableNum}</div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            {user ? <>Bienvenue<br /><span className="text-foreground">{user.name?.split(" ")[0]}</span></> : "Compte requis au paiement"}
          </div>
        </div>
        <div className="max-w-lg mx-auto px-2 pb-2 flex gap-1 overflow-x-auto">
          {CATS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`shrink-0 rounded-full px-4 h-10 text-sm border flex items-center gap-1.5 ${tab === key ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              data-testid={`table-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
              <span className="ml-1 text-[10px] opacity-70">{(catalog[key] || []).length}</span>
            </button>
          ))}
        </div>
      </header>

      {/* Items list */}
      <main className="max-w-lg mx-auto px-4 py-4">
        {barmanMessage && (
          <div
            className="relative overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-4 mb-4 animate-in fade-in duration-500"
            data-testid="table-barman-message"
          >
            <div className="pointer-events-none absolute -top-4 -right-4 h-16 w-16 rounded-full bg-primary/25 blur-2xl" />
            <div className="relative flex gap-3">
              <div className="text-2xl leading-none shrink-0" aria-hidden>🍸</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1">
                  Mot du barman
                </div>
                <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                  {barmanMessage.replace(/\{table\}/gi, String(tableNum))}
                </div>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement du menu…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            Aucun {tab === "cocktails" ? "cocktail" : tab === "beers" ? "bière" : "snack"} disponible pour l'instant.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const kind = CATS.find((c) => c.key === tab).kind;
              const inCart = cart[`${kind}:${it.id}`];
              return (
                <div
                  key={it.id}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3"
                  data-testid={`table-item-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    {it.category && (
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {String(it.category).replace(/_/g, " ")}
                      </div>
                    )}
                    <div className="font-display text-lg leading-tight">{it.name}</div>
                    {it.description && (
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{it.description}</div>
                    )}
                    <div className="text-primary font-medium mt-1">{euro(it.price)}</div>
                  </div>
                  {inCart ? (
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => bump(`${kind}:${it.id}`, -1)} data-testid={`table-item-${it.id}-minus`}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-6 text-center font-medium">{inCart.quantity}</span>
                      <Button size="icon" className="h-9 w-9 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => add(it, kind)} data-testid={`table-item-${it.id}-plus`}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => add(it, kind)}
                      className="h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-4"
                      data-testid={`table-item-${it.id}-add`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Upsell strip — visible once the customer has at least one item.
             Padded bottom so the sticky cart bar doesn't cover the last card. */}
        {lines.length > 0 && (
          <div className="mt-8 pb-40">
            <UpsellStrip context="table_order" cartTotal={subtotal} limit={3} />
          </div>
        )}
      </main>

      {/* Sticky cart / checkout bar */}
      {lines.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/98 backdrop-blur">
          <div className="max-w-lg mx-auto px-4 py-3 space-y-2">
            <details className="rounded-xl bg-card border border-border">
              <summary className="p-3 cursor-pointer text-sm flex items-center justify-between">
                <span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-primary" /> {totalItems} item{totalItems > 1 ? "s" : ""}</span>
                <span className="font-display text-lg text-primary">{euro(subtotal)}</span>
              </summary>
              <div className="px-3 pb-3 space-y-1 max-h-64 overflow-auto">
                {lines.map((l) => (
                  <div key={`${l.kind}:${l.ref_id}`} className="flex items-center gap-2 text-sm">
                    <span className="w-6 text-center text-primary">{l.quantity}×</span>
                    <span className="flex-1 truncate">{l.name}</span>
                    <span className="text-primary">{euro(l.unit_price * l.quantity)}</span>
                    <button onClick={() => bump(`${l.kind}:${l.ref_id}`, -l.quantity)}
                            className="text-destructive p-1" title="Retirer"
                            data-testid={`table-cart-${l.kind}-${l.ref_id}-remove`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </details>

            {/* Loyalty voucher panel — 3 states: rewards available / points but no voucher yet / signed out */}
            {user && vouchers.length > 0 && (() => {
              const totalAvailable = vouchers.reduce((s, v) => s + Number(v.current_balance || 0), 0);
              return (
                <div
                  className="relative overflow-hidden rounded-2xl border-2 border-primary/60 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 shadow-[0_0_24px_-6px_rgba(217,149,51,0.35)]"
                  data-testid="table-vouchers-panel"
                >
                  {/* subtle animated glow */}
                  <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-primary/25 blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="rounded-full bg-primary text-primary-foreground p-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-primary/90 font-semibold">
                          Récompenses fidélité
                        </div>
                        <div className="font-display text-xl text-primary leading-tight">
                          -{euro(totalAvailable)} disponibles !
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground/80 mb-3">
                      Utilisez {vouchers.length === 1 ? "votre bon" : `l'un de vos ${vouchers.length} bons`} dès maintenant pour réduire votre commande.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setChosenVoucher(null)}
                        className={`text-xs rounded-full px-3 h-9 border transition-colors ${
                          !chosenVoucher
                            ? "bg-background border-border text-muted-foreground"
                            : "border-transparent text-muted-foreground/60 hover:text-muted-foreground"
                        }`}
                        data-testid="table-voucher-none"
                      >
                        Ne pas utiliser
                      </button>
                      {vouchers.map((v) => {
                        const selected = chosenVoucher?.id === v.id;
                        return (
                          <button
                            key={v.id}
                            onClick={() => setChosenVoucher(v)}
                            className={`text-xs rounded-full px-3 h-9 border font-medium transition-all ${
                              selected
                                ? "bg-primary text-primary-foreground border-primary shadow-md scale-[1.02]"
                                : "border-primary/50 hover:border-primary hover:bg-primary/10"
                            }`}
                            data-testid={`table-voucher-${v.id}`}
                            title={v.reward_label}
                          >
                            {selected && <span className="mr-1">✓</span>}
                            {v.reward_label || `Bon ${v.code}`}
                            <span className="ml-1.5 font-display text-sm">
                              -{Number(v.current_balance).toFixed(0)}€
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {chosenVoucher && discount > 0 && (
                      <div className="mt-3 flex items-center justify-between rounded-lg bg-emerald-500/15 border border-emerald-500/40 px-3 py-2 text-sm">
                        <span className="text-emerald-200 flex items-center gap-1.5">
                          <span aria-hidden>✨</span> Bon appliqué
                        </span>
                        <span className="text-emerald-300 font-semibold">-{euro(discount)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Progress card — user has points but no voucher yet */}
            {user && vouchers.length === 0 && pointsBalance > 0 && (() => {
              // Cheapest reward = 100 pts (from REWARDS_CATALOG.cocktail).
              const nextThreshold = 100;
              const nextReward = "Cocktail offert (-8€)";
              const remaining = Math.max(0, nextThreshold - pointsBalance);
              const pct = Math.min(100, (pointsBalance / nextThreshold) * 100);
              return (
                <div
                  className="rounded-2xl border border-primary/30 bg-primary/5 p-4"
                  data-testid="table-points-progress"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-primary/90 font-semibold">
                          Ma carte fidélité
                        </div>
                        <div className="text-sm">
                          <b className="text-primary">{pointsBalance}</b> pts cumulés
                        </div>
                      </div>
                    </div>
                    <a href="/compte" className="text-[11px] text-primary underline hover:no-underline">
                      Voir la carte →
                    </a>
                  </div>
                  <div className="h-2 rounded-full bg-background overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary/70 to-primary transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {remaining > 0
                      ? <>Plus que <b className="text-foreground">{remaining} pts</b> pour débloquer <b className="text-primary">{nextReward}</b> 🍸</>
                      : <>Vous avez atteint <b className="text-primary">{nextReward}</b> ! Rendez-vous sur <a href="/compte" className="underline">votre compte</a> pour l'échanger.</>
                    }
                  </div>
                </div>
              );
            })()}

            {/* Signed-out prompt — encourage account creation for future orders */}
            {!user && (
              <div
                className="rounded-xl border border-dashed border-primary/40 bg-primary/[0.03] p-3 text-xs text-muted-foreground flex items-center gap-2"
                data-testid="table-loyalty-signup-hint"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                <span>
                  <a href="/inscription" className="text-primary underline">Créez votre compte</a>
                  {" "}en 30s : cumulez des points sur chaque commande et obtenez des cocktails offerts 🍸
                </span>
              </div>
            )}

            <div className="flex items-center justify-between text-sm px-1">
              <span className="text-muted-foreground">Total à payer</span>
              <span className="font-display text-2xl text-primary">{euro(total)}</span>
            </div>

            <Button
              onClick={openCheckoutModal}
              disabled={checkingOut}
              className="w-full h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium"
              data-testid="table-checkout-btn"
            >
              {total === 0 ? (
                <>🎁 Confirmer la commande gratuite</>
              ) : (
                <>Valider ma commande · {euro(total)}</>
              )}
            </Button>
            <div className="text-[10px] text-muted-foreground text-center">
              {total === 0 ? "Entièrement couvert par votre récompense" : "Choisissez le mode de paiement à l'étape suivante"}
            </div>
          </div>
        </div>
      )}

      {/* Checkout modal — prénom + payment method */}
      {showCheckout && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !checkingOut && setShowCheckout(false)}
          data-testid="table-checkout-modal"
        >
          <div
            className="bg-card border border-border rounded-t-3xl sm:rounded-3xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-2xl mb-1">Presque prêt !</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Table {tableNum} · Total <span className="text-primary font-medium">{euro(total)}</span>
            </p>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 block">
                Votre prénom <span className="text-primary">*</span>
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required maxLength={40}
                placeholder="Ex : Marie"
                className="w-full h-12 rounded-xl bg-background border border-border px-4 text-foreground"
                data-testid="table-checkout-firstname"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Affiché au barman avec votre numéro de table.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowCheckout(false)}
                className="flex-1 rounded-full" disabled={checkingOut}
                data-testid="table-checkout-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={checkout}
                disabled={checkingOut || !firstName.trim()}
                className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="table-checkout-confirm-btn"
              >
                {checkingOut ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
                ) : total === 0 ? (
                  <>Confirmer</>
                ) : (
                  <>💳 Payer {euro(total)}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// -------- Post-payment success page ----------
export function TableSuccess() {
  const { num } = useParams();
  const [status, setStatus] = useState({ state: "polling", tries: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus({ state: "error" }); return; }
    let cancelled = false;
    const tick = async (tries) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/client-orders/status/${sid}`);
        if (data.status === "open" || data.payment_status === "paid") {
          setStatus({ state: "ok", table: data.table_num, total: data.total, lines: data.lines_count });
          return;
        }
        if (tries >= 12) { setStatus({ state: "slow" }); return; }
        setTimeout(() => tick(tries + 1), 2500);
      } catch { setTimeout(() => tick(tries + 1), 3000); }
    };
    tick(0);
    return () => { cancelled = true; };
    // Only runs once on mount — session_id is read directly from window.location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="table-success-page">
      <div className="max-w-sm w-full text-center">
        {status.state === "polling" ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl mb-2">Confirmation du paiement…</h1>
            <p className="text-sm text-muted-foreground">Un instant, on discute avec Stripe.</p>
          </>
        ) : status.state === "ok" ? (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-5">
              <Sparkles className="w-9 h-9 text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl mb-2">Merci !</h1>
            <p className="text-sm text-muted-foreground mb-4">
              Votre commande est en préparation.<br />
              Nos équipes vous l'apportent à votre table {num || status.table} très vite.
            </p>
            <div className="rounded-xl border border-border bg-card p-3 text-sm mb-6">
              {status.lines} item{status.lines > 1 ? "s" : ""} · {Number(status.total || 0).toFixed(2)} €
            </div>
            <div className="mb-6"><PushToggle variant="inline" /></div>
            <a href={`/table/${num || status.table}`}
               className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-3 h-3" /> Repasser une commande
            </a>
          </>
        ) : status.state === "slow" ? (
          <>
            <h1 className="font-display text-2xl mb-2">Traitement en cours</h1>
            <p className="text-sm text-muted-foreground mb-4">
              La confirmation prend un peu plus de temps que prévu. Votre paiement a bien été enregistré si vous voyez ce message —
              le bar a été alerté. Vous pouvez fermer cette page en toute sécurité.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl mb-2">Erreur</h1>
            <p className="text-sm text-muted-foreground">Session Stripe introuvable.</p>
          </>
        )}
      </div>
    </div>
  );
}
